import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  type OnChanges,
  type OnDestroy,
  Output,
} from '@angular/core';
import { FormBuilder, type FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateRemoteUserLocation } from '../../../shared/models/api/remote-user-location.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { RemoteUserLocationService } from '../../../shared/services/api/remote-user-location.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { CreateRemoteUserLocationFormValue } from './remote-user-location-create.models';

@Component({
  selector: 'opa-remote-user-location-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './remote-user-location-create.component.html',
  styleUrl: './remote-user-location-create.component.scss',
})
export class RemoteUserLocationCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly remoteUserLocationService = inject(RemoteUserLocationService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<RemoteUserLocationCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const remoteUserLocation = this.formValueToRemoteUserLocation(formValue);
    this.subSink.sink = this.remoteUserLocationService
      .createRemoteUserLocation(remoteUserLocation)
      .pipe(
        tap({
          next: (createRemoteUserLocation) => {
            this.toastManager.addMessage({
              text: `Remote User Location '${createRemoteUserLocation.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateRemoteUserLocationFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateRemoteUserLocationFormValue>>({
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToRemoteUserLocation(
    form: CreateRemoteUserLocationFormValue,
  ): CreateRemoteUserLocation {
    const remoteUserLocation: CreateRemoteUserLocation = {
      description: form.description!,
      name: form.name!,
    };
    return remoteUserLocation;
  }
}
